import { useEffect, useState } from 'react'
import { Options } from 'react-select'
import { RequestOptions, searchAddress } from '@msaf/generic-search-common'
import debounce from 'lodash.debounce'
import { AUTOCOMPLETE_MIN_CHARS, Button, Typeahead, TypeaheadOption } from '@msaf/core-react'
import { getAddressCoordinates } from '../../../../generic-search-common/src'

export type AddressOption = { label: string; value: string }

export type LocationMetaData = {
  lat: number
  lng: number
  extents?: {
    latMin: number
    lngMin: number
    latMax: number
    lngMax: number
  }
}

type GotoControlProps = {
  requestOptions?: RequestOptions
  onAddressChange?: (option: AddressOption) => void
  goToLocation?: (locationMetaData: LocationMetaData) => void | undefined
  onSearch?: (selectedAddress: AddressOption) => void
  selectedAddress?: AddressOption
  labelledBy?: string
  autoFocus?: boolean
  placeholderText?: string
}

export default function GotoControl({
  requestOptions,
  onAddressChange,
  goToLocation,
  onSearch,
  selectedAddress,
  labelledBy,
  autoFocus = true,
  placeholderText = 'Enter address or location',
}: GotoControlProps) {
  const [selectedOption, setSelectedOption] = useState<AddressOption | null>(null)

  const fetchSuggestions = async (inputValue: string, callback: (options: AddressOption[]) => void) => {
    const result = await searchAddress(inputValue, requestOptions)
    if (!result.error) {
      const options = result.data.map(({ text, key }: any) => ({ label: text, value: key }))
      callback(options)
    }
  }

  const loadOptions = (inputValue: string, callback: (options: Options<TypeaheadOption>) => void) => {
    if (inputValue == null || inputValue === '' || inputValue.length < AUTOCOMPLETE_MIN_CHARS) return callback([])
    if (inputValue.length >= AUTOCOMPLETE_MIN_CHARS) {
      fetchSuggestions(inputValue, callback)
    }
  }

  const handleAddressSelection = async (address: AddressOption) => {
    setSelectedOption(address)

    if (onAddressChange) {
      onAddressChange(address)
    }

    const { label, value } = address

    if (label && value) {
      const { data } = requestOptions && (await getAddressCoordinates(label, value, requestOptions))
      if (data) {
        const metaData = {
          lat: data.lat,
          lng: data.lng,
          extents: data.extents,
        }
        goToLocation && goToLocation(metaData)
      }
    }
  }

  const handleClick = () => {
    onSearch && selectedOption && onSearch(selectedOption)
  }

  useEffect(() => {
    if (selectedAddress) {
      handleAddressSelection(selectedAddress)
    }
    // Disabling due to suggestion of handleAddressSuggestion
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddress])

  return (
    <>
      <Typeahead
        selectType='async'
        labelledBy={labelledBy ?? 'gotoAddress'}
        handleChange={handleAddressSelection}
        selectedOption={selectedOption}
        options={[]}
        loadOptions={debounce(loadOptions, 300)}
        isClearable
        autoFocus={autoFocus}
        placeholder={placeholderText}
      />
      {onSearch && <Button onClick={handleClick} label='Search' />}
    </>
  )
}
